/* eslint no-console: "off" */
import { capitalize } from '@comp/Filters.js'
import ConfigManager from '@config/ConfigManager'
import { addBreadcrumb, captureException, setTag, withScope } from '@sentry/vue'

/**
 * The Logging service logs messages to the console.
 * Debug messages can be toggled on or off by setting `localStorage.debug`
 * to [log, info, warn, error] or "log, info"
 */
class Logging {
    constructor() {
        const levels = localStorage?.debug

        this.logLevel = levels
            ? Array.isArray(levels)
                ? levels
                : levels.split(',').map(item => item.trim())
            : ['error']

        this.Tags = {
            Fixtures: 'Fixtures',
            Content: 'Content'
        }
    }

    /** @private */
    _log(level = 'log', message) {
        let Levels = ['log', 'info', 'warn', 'error']

        if (!Levels.includes(level)) {
            throw new Error('Invalid logging level:', level)
        }

        let displayLevel = `${level.toUpperCase()} »`

        console[level](displayLevel, ...message)
    }

    /**
     * Log message
     * @param message {String|Array}
     */
    log(...message) {
        if (this.logLevel.includes('log') || ConfigManager.isMockingServer) {
            this._log('log', message)
        }
    }

    /**
     * Log info message
     * @param message {String|Array}
     */
    info(...message) {
        if (this.logLevel.includes('info')) {
            this._log('info', message)
        }
    }

    /**
     * Log warning message
     * @param message {String|Array}
     */
    warn(...message) {
        if (this.logLevel.includes('warn')) {
            this._log('warn', message)
        }
    }

    /**
     * @param {boolean} sendToMixpanel - Deprecated: All errors should be sent only to Sentry
     * */
    error(message, sendToMixpanel = false, tags = []) {
        this._log('error', [message])

        withScope((scope) => {
            // Scoped so tags don't persist between errors
            const sentryTags = this.#getAllTags(tags, message)
            if (sentryTags.length) {
                this.setSentryTags(sentryTags, scope)
            }

            captureException(new Error(message))
        })
    }

    addSentryBreadcrumbs(messages, category = 'default') {
        if (!Array.isArray(messages)) {
            messages = [messages]
        }

        messages.forEach(message => {
            addBreadcrumb({
                category: category,
                message: message,
                level: 'info'
            })
        })
    }

    /**
     * Sets the provided tags in Sentry, either in the provided scope or globally
     * @param {Array} tags - Array of tag objects with 'tag' and 'value' properties
     * @param {Object} scope - Optional Sentry scope to set tags in, otherwise sets globally
     */
    setSentryTags(tags, scope) {
        tags.forEach(({ tag, value }) => {
            if (scope) {
                scope.setTag(tag, value)

                return
            }
            setTag(tag, value)
        })
    }

    /**
     * Gets all valid tags including the inferred message tag, removing duplicates
     * @private
     * @param {Array} tags - Array of tag objects with 'tag' and 'value' properties
     * @param {String} message - The log message to infer tag from
     * @return {Array} - Array of tag objects with 'tag' and 'value' properties
     */
    #getAllTags(tags, message) {
        const tagMap = new Map()
        const unsetTagValue = '-'

        tags.forEach(tag => {
            if (this.Tags[tag.tag]) {
                if (!tag.value) {
                    this.warn(`Logging.setSentryTags: Tag value is empty for tag ${tag.tag}`)
                }

                const value = tag.value || unsetTagValue
                tagMap.set(tag.tag, value)
            }
        })

        if (typeof message === 'string') {
            const messageTag = this.#getMessageTag(message)

            if (messageTag) {
                tagMap.set(messageTag, unsetTagValue)
            }
        }

        return Array.from(tagMap, ([tag, value]) => ({ tag, value }))
    }

    /**
     * Get the inferred tag from the message prefix
     * @private
     * @param {String} message - The error message
     */
    #getMessageTag(message) {
        if (!message) {
            return
        }

        const tagKey = capitalize(message.split(':')[0])

        return this.Tags[tagKey]
    }
}

export default new Logging()
